<template>
  <CatalogCard
    :image="variant.image"
    :name="variant.name"
    :price="displayPrice"
    :coins-amount="isCoinsVariant ? variant.attributes.amount : 0"
    :force-pointer-cursor="isCoinsType"
    v-bind="catalogCardProps"
    @increase-quantity="increaseQuantity"
    @decrease-quantity="decreaseQuantity"
    @card-click="handleCardClick">
    <template v-if="isCoinsType" #price>
      <span class="font-weight-bold">
        {{ coinsPerCurrency }}
        <CoinIcon :size="17" />
        {{ regularCoinCurrency }}
      </span>
    </template>
    <template v-if="isCoinsType" #actions>
      <v-text-field
        ref="coinInput"
        v-model.number="coinAmountInput"
        :suffix="coinCurrencyLabel"
        type="number"
        step="0.01"
        min="0"
        hide-details
        dense
        outlined
        rounded
        @input="handleCoinQuantityInput"
        @click.native.stop
        class="coin-quantity-input" />
    </template>
  </CatalogCard>
</template>

<script>
import CoinIcon from '@/components/CoinIcon.vue'
import CatalogCard from './CatalogCard.vue'
import { COIN_TYPES } from '@/constants/coins'
import { formatPrice } from '@/utils/price'

export default {
  name: 'CatalogVariantCard',
  components: {
    CatalogCard,
    CoinIcon,
  },
  props: {
    variant: { type: Object, required: true },
    quantity: { type: Number, default: 0 },
    isCoinsVariant: { type: Boolean, required: true },
    isDigitalItem: { type: Boolean, required: true },
    arenaCurrency: { type: Object, default: () => ({}) },
  },
  data: () => ({
    coinAmountInput: 0,
    quantityDebounce: null,
  }),
  watch: {
    quantity: {
      immediate: true,
      handler() {
        this.syncCoinAmountInput()
      },
    },
    coinsPerCurrency() {
      this.syncCoinAmountInput()
    },
  },
  computed: {
    catalogCardProps() {
      return {
        quantity: this.quantity,
        availableStock: this.availableStock,
        isStockDisabled: !this.variant?.track_stock,
        currency: this.currency,
        forceShowActions: this.isCoinsType,
        disableCardActivation: this.isCoinsType,
        weekdays: this.variant?.attributes?.weekdays,
        packType: this.variant?.pack_type || null,
      }
    },
    isCoinsType() {
      return (
        this.isCoinsVariant &&
        this.variant?.attributes?.type === COIN_TYPES.coins
      )
    },
    displayPrice() {
      const price = this.variantPriceInfo.gross_price
      return price !== undefined && price !== null ? formatPrice(price) : '—'
    },
    currency() {
      const currency = this.variantPriceInfo.currency
      return currency.symbol
    },
    variantPriceInfo() {
      const prices = this.variant.prices
      const onlyPrice = prices[0]
      return onlyPrice
    },
    availableStock() {
      const available = this.variant?.stock?.available
      const parsed = Number(available)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    coinsPerCurrency() {
      const coins = Number(this.arenaCurrency?.coins_per_currency)
      return Number.isFinite(coins) && coins > 0 ? coins : 0
    },
    coinCurrencyLabel() {
      return this.arenaCurrency?.symbol || this.arenaCurrency?.code
    },
    regularCoinCurrency() {
      const currency = this.coinCurrencyLabel || ''
      return `/ 1 ${currency}`
    },
    coinPriceLabel() {
      const coinsValue = this.coinsPerCurrency || 0
      const currency = this.coinCurrencyLabel || ''
      return `${coinsValue} / 1 ${currency}`.trim()
    },
  },
  methods: {
    syncCoinAmountInput() {
      if (!this.isCoinsType || !this.coinsPerCurrency) {
        this.coinAmountInput = 0
        return
      }
      const baseQuantity = Number(this.quantity) || 0
      this.coinAmountInput = baseQuantity / this.coinsPerCurrency
    },
    handleCoinQuantityInput(value) {
      if (!this.isCoinsType) return
      const numericValue = Number(value)
      if (!this.coinsPerCurrency || Number.isNaN(numericValue)) {
        this.emitQuantity(0)
        return
      }
      const calculatedQuantity = Math.max(
        0,
        Math.round(numericValue * this.coinsPerCurrency),
      )
      this.emitQuantity(calculatedQuantity)
    },
    emitQuantity(quantity) {
      if (this.isCoinsType) {
        clearTimeout(this.quantityDebounce)
        this.quantityDebounce = setTimeout(() => {
          this.$emit('update-quantity', {
            variant: this.variant,
            quantity,
          })
        }, 500)
      } else {
        this.$emit('update-quantity', {
          variant: this.variant,
          quantity,
        })
      }
    },
    handleCardClick() {
      if (!this.isCoinsType || !this.$refs.coinInput) return
      this.$refs.coinInput.focus()
    },
    decreaseQuantity() {
      const nextQuantity = Math.max(0, this.quantity - 1)
      this.emitQuantity(nextQuantity)
    },
    increaseQuantity() {
      const nextQuantity = this.quantity + 1
      if (
        !this.isDigitalItem &&
        this.variant?.track_stock &&
        nextQuantity > this.availableStock
      ) {
        this.$store.dispatch('showSnackbar', {
          color: 'error',
          message: this.$t('purchaseV2.catalog.insufficientStock'),
          icon: 'error',
        })
        return
      }
      this.emitQuantity(nextQuantity)
    },
  },
}
</script>

<style lang="scss" scoped>
.coin-quantity-input {
  margin-right: 20% !important;
  margin-left: 20% !important;
  margin-bottom: 8px !important;
}

::v-deep {
  .catalog-card__content {
    gap: 0;
  }

  .catalog-card__info {
    height: 2.625rem;
  }
}
</style>
